import type {
  CartMutation,
  CartOperation,
} from '../../../entities/cart/config/mutations';

export type SyncReason = 'cart' | 'session';
type OwnedSnapshot = { owner_context: string };
export type PendingMutation = {
  mutation: CartMutation;
  createdAt: number;
  sequence: number;
  tabId: string;
};
export type MutationRejection = {
  mutationId: string;
  tabId: string;
  status: number;
  message: string;
};
export interface CartSyncDeps<T extends OwnedSnapshot> {
  getCart(): Promise<T>;
  mutateCart(mutation: CartMutation): Promise<T>;
  readPending(): PendingMutation[];
  putPending(pending: PendingMutation): void;
  removePending(id: string): void;
  recordRejection(rejection: MutationRejection): void;
  takeRejection(): MutationRejection | null;
  exclusive<R>(work: () => Promise<R>): Promise<R>;
  publish(reason: SyncReason): void;
  applySnapshot(snapshot: T): void;
  onSyncState(state: { pending: number; error: Error | null }): void;
  createMutationId(): string;
  now(): number;
  tabId: string;
}
export interface CartSync {
  start(): Promise<void>;
  enqueue(operation: CartOperation): Promise<void>;
  refresh(): void;
  invalidate(reason: SyncReason): void;
  flush(): Promise<void>;
  runBoundary<R>(reason: SyncReason, request: () => Promise<R>): Promise<R>;
  stop(): void;
}
const NETWORK_ERROR =
  'Не удалось сохранить изменения корзины. Проверьте соединение и повторите.';
const CONTEXT_ERROR =
  'Вход или выход изменил корзину. Неотправленное действие отменено; повторите его в текущей корзине.';

type Intent = { operation: CartOperation; id: string; generation: number };
export function createCartSync<T extends OwnedSnapshot>(
  deps: CartSyncDeps<T>,
): CartSync {
  let active = false,
    epoch = 0,
    generation = 0,
    lifecycle = 0,
    sequence = 0,
    lastTime = 0;
  let owner: string | null = null,
    running: Promise<void> | null = null;
  let error: Error | null = null;
  let completedEpoch = -1;
  const intents: Intent[] = [];

  function state() {
    if (!active) return;
    let pending = intents.length;
    try {
      pending += deps.readPending().length;
    } catch {
      pending += 1;
    }
    deps.onSyncState({ pending, error });
  }
  function fail(cause: unknown): Error {
    const api = cause as { status?: number };
    error =
      api?.status === 422 || api?.status === 409 || api?.status === 426
        ? (cause as Error)
        : new Error(NETWORK_ERROR);
    state();
    return error;
  }
  function persistIntents() {
    if (!owner) return;
    while (intents.length) {
      const intent = intents[0];
      if (intent.generation !== generation) {
        intents.shift();
        throw Object.assign(new Error(CONTEXT_ERROR), { status: 409 });
      }
      lastTime = Math.max(deps.now(), lastTime + 1);
      deps.putPending({
        mutation: {
          mutation_id: intent.id,
          owner_context: owner,
          operation: intent.operation,
        },
        createdAt: lastTime,
        sequence: ++sequence,
        tabId: deps.tabId,
      });
      intents.shift();
    }
  }
  async function readFresh(life: number) {
    const version = epoch,
      scope = generation;
    const snapshot = await deps.getCart();
    if (!active || life !== lifecycle || scope !== generation) return false;
    if (typeof snapshot.owner_context !== 'string' || !snapshot.owner_context)
      throw Object.assign(
        new Error(
          'Версия сайта изменилась. Обновите страницу, чтобы продолжить работу с корзиной.',
        ),
        { status: 426 },
      );
    owner = snapshot.owner_context;
    if (version !== epoch || intents.length || deps.readPending().length)
      return false;
    deps.applySnapshot(snapshot);
    completedEpoch = epoch;
    return true;
  }
  // Caller owns the same cross-tab lock for initialization, mutations, and boundaries.
  async function drain(life: number) {
    let rejected: Error | null = null;
    while (active && life === lifecycle) {
      if (!owner) await readFresh(life);
      if (!active || life !== lifecycle) return;
      if (!owner) continue; // session invalidated the GET
      try {
        persistIntents();
      } catch (cause) {
        if ((cause as { status?: number }).status !== 409) throw cause;
        rejected = cause as Error;
        continue;
      }
      const next = deps
        .readPending()
        .sort(
          (a, b) =>
            a.createdAt - b.createdAt ||
            a.sequence - b.sequence ||
            a.tabId.localeCompare(b.tabId) ||
            a.mutation.mutation_id.localeCompare(b.mutation.mutation_id),
        )[0];
      if (!next) {
        if (!(await readFresh(life))) continue;
        const remoteRejection = deps.takeRejection();
        if (remoteRejection)
          throw Object.assign(new Error(remoteRejection.message), {
            status: remoteRejection.status,
          });
        if (rejected) throw rejected;
        return;
      }
      try {
        await deps.mutateCart(next.mutation);
      } catch (cause) {
        const api = cause as { status?: number; errorCode?: string };
        if (api.status !== 422 && api.status !== 409) throw cause; // uncertain result: retain this exact UUID, stop here
        rejected =
          api.errorCode === 'cart_context_changed'
            ? Object.assign(new Error(CONTEXT_ERROR), { status: 409 })
            : (cause as Error);
        // Persist before deleting pending: the initiating tab may acquire the lock
        // before BroadcastChannel/storage delivers the invalidation signal.
        if (next.tabId !== deps.tabId)
          deps.recordRejection({
            mutationId: next.mutation.mutation_id,
            tabId: next.tabId,
            status: api.status,
            message: rejected.message,
          });
        owner = null;
      }
      deps.removePending(next.mutation.mutation_id);
      deps.publish('cart');
      state();
      // Read the shared queue again: another tab may have appended an action while awaiting HTTP.
    }
  }
  function flush(): Promise<void> {
    if (running) return running;
    const life = lifecycle;
    error = null;
    state();
    running = deps
      .exclusive(() => drain(life))
      .catch((cause) => {
        throw fail(cause);
      })
      .finally(() => {
        running = null;
        state();
        if (
          active &&
          !error &&
          (life !== lifecycle || completedEpoch !== epoch)
        )
          refresh();
      });
    return running;
  }
  function refresh() {
    if (active) void flush().catch(() => {});
  }
  function invalidate(reason: SyncReason) {
    epoch++;
    if (reason === 'session') {
      generation++;
      owner = null;
    }
    refresh();
  }
  return {
    start() {
      if (!active) {
        active = true;
        lifecycle++;
        owner = null;
        if (running) return running.catch(() => {}).then(flush);
      }
      return flush();
    },
    enqueue(operation) {
      epoch++;
      intents.push({ operation, id: deps.createMutationId(), generation });
      try {
        persistIntents();
      } catch (cause) {
        return Promise.reject(fail(cause));
      }
      state();
      return flush();
    },
    refresh,
    invalidate,
    flush,
    async runBoundary(reason, request) {
      await flush();
      let requested = false;
      return deps
        .exclusive(async () => {
          try {
            await drain(lifecycle);
          } catch (cause) {
            throw fail(cause);
          }
          requested = true;
          const result = await request();
          epoch++;
          if (reason === 'session') {
            generation++;
            owner = null;
          }
          deps.publish(reason);
          // The order/login already succeeded. A failed refresh must never turn it into a retryable submission.
          try {
            await drain(lifecycle);
          } catch (cause) {
            fail(cause);
          }
          return result;
        })
        .catch((cause) => {
          throw requested ? cause : fail(cause);
        });
    },
    stop() {
      active = false;
      lifecycle++;
      epoch++;
      owner = null;
    },
  };
}
